// ============================================================
// services/usuarioFotoService.ts
// Subir / eliminar foto de perfil de un usuario (MinIO).
// ============================================================

import { httpClient, HttpError } from 'utils/httpClient';
import { UsuarioListItem } from 'types/usuario.types';

export const TIPOS_FOTO_PERMITIDOS = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const TAMANIO_FOTO_MAX_MB_DEFAULT = 2;

/** Valida un archivo antes de enviarlo. Devuelve mensaje de error o null si OK. */
export function validarArchivoFoto(file: File, maxMB = TAMANIO_FOTO_MAX_MB_DEFAULT): string | null {
  if (file.size === 0) return 'El archivo está vacío.';
  if (!(TIPOS_FOTO_PERMITIDOS as readonly string[]).includes(file.type)) {
    return 'Formato no permitido. Solo JPG, PNG o WebP.';
  }
  const maxBytes = maxMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return `El archivo supera el tamaño máximo de ${maxMB} MB.`;
  }
  return null;
}

export const usuarioFotoService = {

  // POST /usuarios/{id}/foto — multipart con campo "file"
  async subir(idUsuario: number, file: File): Promise<UsuarioListItem> {
    const fd = new FormData();
    fd.append('file', file);
    return httpClient.upload<UsuarioListItem>(`/usuarios/${idUsuario}/foto`, fd);
  },

  // DELETE /usuarios/{id}/foto
  async eliminar(idUsuario: number): Promise<UsuarioListItem> {
    return httpClient.delete<UsuarioListItem>(`/usuarios/${idUsuario}/foto`);
  },

  /**
   * Tamaño máximo en MB leído de la config institucional.
   * Si la consulta falla, retorna el default (2 MB).
   */
  async getTamanioMaxMB(): Promise<number> {
    try {
      const data = await httpClient.get<{ valor: string } | string>(
        '/configuraciones/MINIO_FOTO_TAMANIO_MAX_MB',
      );
      const v = typeof data === 'string' ? data : data?.valor;
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : TAMANIO_FOTO_MAX_MB_DEFAULT;
    } catch (err) {
      if (err instanceof HttpError && err.status !== 404) {
        // 404 = la clave aún no existe → usamos el default sin loggear.
        // Otros errores los ignoramos en silencio (no es bloqueante).
      }
      return TAMANIO_FOTO_MAX_MB_DEFAULT;
    }
  },
};
